import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  remove,
  findByCodTurmaAndCodCronogramaData,
  findByCodTurmaAndCodCronogramaDataVersao2,
} from "./api-cronograma";
import { dayOfWeekName, timeFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";
import CronogramaForm from "./CronogramaForm";
import CronogramaTableSelect from "./CronogramaTableSelect";
import CronogramaTableV2 from "./CronogramaTableV2";
import { mensagemDe, porDiaEHorario } from "./cronogramaUtil";

const LISTA = "lista";
const GRADE = "grade";

/** Aulas do cronograma de um período da turma, em lista (editável) ou grade semanal. */
export default function CronogramaTable({ turma, cronogramaData, periodoAnterior }) {
  const [visao, setVisao] = useState(LISTA);
  const [cronogramas, setCronogramas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [editando, setEditando] = useState(null);
  const [openCopiar, setOpenCopiar] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    const request =
      visao === GRADE
        ? findByCodTurmaAndCodCronogramaDataVersao2(turma.codigo, cronogramaData.codigo)
        : findByCodTurmaAndCodCronogramaData(turma.codigo, cronogramaData.codigo);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setCronogramas([...data].sort(porDiaEHorario));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [turma.codigo, cronogramaData.codigo, visao, reloadKey]);

  const handleDelete = (cronograma) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a ${cronograma.cronogramaAula?.ordemAula}ª aula de ${dayOfWeekName(cronograma.diaSemana)}?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(cronograma.codigo).then((data) => {
              if (data?.error) {
                errorMessage(mensagemDe(data));
                return;
              }
              setCronogramas((prevState) => prevState.filter((c) => c.codigo !== cronograma.codigo));
              success("Aula removida do cronograma.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleSaved = (data, eNovo) => {
    setEditando(null);
    setCronogramas((prevState) =>
      (eNovo ? [...prevState, data] : prevState.map((c) => (c.codigo === data.codigo ? data : c))).sort(porDiaEHorario),
    );
  };

  const renderLista = () => {
    if (cronogramas.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
          Nenhuma aula cadastrada neste período.
        </Typography>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth: 720 }} aria-label="Aulas do cronograma">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell>Dia</TableCell>
              <TableCell>Aula</TableCell>
              <TableCell>Disciplina</TableCell>
              <TableCell>Professor</TableCell>
              <TableCell>Local</TableCell>
              <TableCell align="right" className="no-print" sx={{ width: 96 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {cronogramas.map((c, index) => {
              const novoDia = index === 0 || cronogramas[index - 1].diaSemana !== c.diaSemana;
              return (
                <TableRow
                  key={c.codigo}
                  hover
                  sx={novoDia && index > 0 ? { "& td": { borderTop: 2, borderTopColor: "divider" } } : undefined}
                >
                  <TableCell sx={{ fontWeight: novoDia ? 600 : 400, color: novoDia ? "text.primary" : "text.disabled" }}>
                    {dayOfWeekName(c.diaSemana)}
                  </TableCell>
                  <TableCell>
                    {c.cronogramaAula?.ordemAula}ª · {timeFormat(c.cronogramaAula?.horarioInicio)} -{" "}
                    {timeFormat(c.cronogramaAula?.horarioFim)}
                  </TableCell>
                  <TableCell>{c.tdp?.disciplina?.descricao}</TableCell>
                  <TableCell>{c.tdp?.usuario?.nome ?? "--"}</TableCell>
                  <TableCell>{c.local?.descricao ?? "--"}</TableCell>
                  <TableCell align="right" className="no-print">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => setEditando(c)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton size="small" onClick={() => handleDelete(c)}>
                        <DeleteOutlineIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <CronogramaForm
        cronograma={editando}
        turma={turma}
        cronogramaData={cronogramaData}
        onClose={() => setEditando(null)}
        onSaved={handleSaved}
      />
      <CronogramaTableSelect
        open={openCopiar}
        onClose={() => setOpenCopiar(false)}
        turma={turma}
        periodoAnterior={periodoAnterior}
        periodoAtual={cronogramaData}
        onCopied={() => {
          setOpenCopiar(false);
          setReloadKey((key) => key + 1);
        }}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        mb={2}
        className="no-print"
      >
        <ToggleButtonGroup size="small" exclusive value={visao} onChange={(event, value) => value && setVisao(value)}>
          <ToggleButton value={LISTA}>Lista</ToggleButton>
          <ToggleButton value={GRADE}>Grade semanal</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {visao === LISTA ? (
            <>
              <Tooltip title={periodoAnterior ? "" : "Não existe período anterior para copiar."}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyOutlinedIcon />}
                    disabled={!periodoAnterior}
                    onClick={() => setOpenCopiar(true)}
                  >
                    Copiar do Período Anterior
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ eNovo: true })}>
                Adicionar Aula
              </Button>
            </>
          ) : (
            <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
              Imprimir
            </Button>
          )}
        </Stack>
      </Stack>

      {isLoading ? (
        <Box py={6}>
          <Loading description="Carregando cronograma..." />
        </Box>
      ) : visao === LISTA ? (
        renderLista()
      ) : (
        <CronogramaTableV2 cronogramas={cronogramas} />
      )}
    </Box>
  );
}
